import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import { FavouriteButton } from '@noths/polaris-design-system-candidate-components';
import Image from 'next/image';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { ThumbnailsContainer } from 'src/components/molecules/Thumbnails/ThumbnailsContainer';
import type {
  MediaItem,
  NextOrPreviousPayload,
  OpenModalPayload,
  SetCurrentImagePayload,
} from 'src/components/organisms/ProductImagesCarousel/types';
import { selectCurrentImage, selectNavigatedBy } from 'src/redux/productImagesCarousel/selectors';
import { PRODUCT_IMAGES_SECTION_LABEL_SINGLE } from './constants/copy';
import {
  PRODUCT_IMAGE_TABPANEL_CLASS,
  PRODUCT_IMAGES_TABPANEL_CONTAINER_ID,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from './constants/elementIds';
import { mediaKey } from './media/buildProductMedia';
import { useProductImagesCarousel } from './useProductImagesCarousel';
import * as styles from './ProductImagesCarousel.styles';

export interface ProductImagesCarouselStateProps {
  favouriteProduct: boolean;
  media: MediaItem[];
  productCode: number;
}

export interface ProductImagesCarouselDispatchProps {
  onFavouriteClick: (productCode: number) => void;
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onOpenModal: (payload: OpenModalPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

export type ProductImagesCarouselProps = ProductImagesCarouselStateProps &
  ProductImagesCarouselDispatchProps;

export const ProductImagesCarousel = ({
  favouriteProduct,
  media,
  onFavouriteClick,
  onNextImage,
  onOpenModal,
  onPrevImage,
  productCode,
  setCurrentImage,
}: ProductImagesCarouselProps) => {
  const currentImage = useSelector(selectCurrentImage);
  const navigatedBy = useSelector(selectNavigatedBy);
  const totalImages = media.length;
  const location = 'Main image';
  const [imageFocus, setImageFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  const [loadedImages, setImageLoaded] = React.useState<Record<number, boolean>>({});

  useEffect(() => {
    if (currentImage > 0) {
      // User has navigated past the first image
      setHasInteractedWithCarousel(true);
    }
  }, [currentImage]);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage != imageIndex) {
        setCurrentImage({ imageIndex, navigationType: 'swipe', location });
      }
    },
    currentImage,
    navigatedBy,
    totalImages,
  );

  return (
    <>
      <PreventSwipeNavigation aria-live="polite" css={[styles.imagesWrapper]}>
        <section aria-label={PRODUCT_IMAGES_SECTION_LABEL_SINGLE} css={[styles.carouselContainer]}>
          <div
            css={[styles.mainImageWrapperWithThumbnails, styles.allRoundedCorners]}
            data-image-focus={imageFocus}
            id={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
            ref={carouselContainerRef}
          >
            {media.map((item, i) => {
              if (item.type === 'video') {
                return (
                  <div
                    className={PRODUCT_IMAGE_TABPANEL_CLASS}
                    css={styles.carouselSlide}
                    data-image-focus={imageFocus}
                    data-item-index={i}
                    id={`${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
                    key={mediaKey(item)}
                    ref={(node) => {
                      carouselItemRefs.current[i] = node as HTMLElement;
                    }}
                  >
                    <ProductVideoSlide
                      contentPosition={i + 1}
                      isActive={currentImage === i}
                      location="Main image"
                      onExpand={() => onOpenModal({ location: 'Main image' })}
                      playbackId={item.playbackId}
                      posterUrl={item.posterUrl}
                      title={item.title}
                      totalOptions={totalImages}
                    />
                  </div>
                );
              }

              const { href: src, title } = item;

              return (
                <button
                  aria-label={`${title}, ${i + 1} of ${totalImages}`}
                  className={PRODUCT_IMAGE_TABPANEL_CLASS}
                  css={styles.carouselSlide}
                  data-image-focus={imageFocus}
                  data-item-index={i}
                  id={`${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
                  key={mediaKey(item)}
                  onBlur={() => setImageFocus(false)}
                  onClick={() => onOpenModal({ location: 'Main image' })}
                  onFocus={(e) => setImageFocus(isKeyboardTriggeredEvent(e))}
                  ref={(node) => {
                    carouselItemRefs.current[i] = node as HTMLElement;
                  }}
                  tabIndex={currentImage === i ? undefined : -1}
                  type="button"
                >
                  <Image
                    alt={`${title}, ${i + 1} of ${totalImages}`}
                    css={styles.mainImage}
                    fill={true}
                    onLoad={() => setImageLoaded({ ...loadedImages, [i]: true })}
                    priority={i === 0}
                    src={getProductImageURL(
                      src,
                      i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                    )}
                    unoptimized
                  />
                </button>
              );
            })}
          </div>
          {totalImages > 1 && (
            <>
              <PreviousButton onClick={() => onPrevImage({ location, totalImages })} />
              <NextButton onClick={() => onNextImage({ location, totalImages })} />
              <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
            </>
          )}
          <FavouriteButton
            active={favouriteProduct}
            css={styles.favouriteButton}
            onClick={() => onFavouriteClick(productCode)}
            size="lg"
          />
        </section>
      </PreventSwipeNavigation>
      {totalImages > 1 && (
        <div css={styles.imageThumbnails}>
          <ThumbnailsContainer location={location} />
        </div>
      )}
    </>
  );
};
